import GameWorld from 'asteroids-shared/GameWorld'
import {
  WORLD_WIDTH, WORLD_HEIGHT, PLAYER_COLORS, ASTEROID_INITIAL_COUNT,
  CTF_ROUND_DURATION_MS, CTF_FLAG_PICKUP_BONUS, TICK_MS
} from 'asteroids-shared/constants'
import { decodeReplayBinary } from 'asteroids-shared/ReplayBinary'
import CoopMode from 'asteroids-shared/gamemodes/CoopMode'
import DeathMatchMode from 'asteroids-shared/gamemodes/DeathMatchMode'
import CTFMode from 'asteroids-shared/gamemodes/CTFMode'

function buildPolicy(decoded) {
  const p = decoded.policySnapshot ?? {}
  const mode = decoded.gameMode
  if (mode === 'coop') {
    return new CoopMode({
      initialLives: p.initialLives ?? 3,
      shieldsEnabled: p.shieldsEnabled === true
    })
  }
  if (mode === 'deathmatch') {
    return new DeathMatchMode({
      roundDuration: p.roundDuration ?? 180000,
      killBonus: p.killBonus ?? 500,
      asteroidCount: p.asteroidCount ?? ASTEROID_INITIAL_COUNT,
      asteroidsEnabled: p.asteroidsEnabled !== false,
      shieldsEnabled: p.shieldsEnabled === true
    })
  }
  if (mode === 'ctf') {
    return new CTFMode({
      roundDuration: p.roundDuration ?? CTF_ROUND_DURATION_MS,
      killBonus: p.killBonus ?? 1,
      captureBonus: p.captureBonus ?? 10,
      pickupBonus: p.pickupBonus ?? CTF_FLAG_PICKUP_BONUS,
      asteroidsEnabled: p.asteroidsEnabled === true,
      shieldsEnabled: p.shieldsEnabled === true
    })
  }
  return new CoopMode()
}

function normalizePlayers(decoded) {
  return decoded.players.map((p, i) => ({
    id: p.id,
    name: p.name || `Player ${i + 1}`,
    slot: p.slot ?? i,
    color: p.color || PLAYER_COLORS[p.slot ?? i] || '#fff',
    teamId: p.teamId ?? null,
    teamName: p.teamName ?? null
  }))
}

// Deterministic lockstep playback from an uncompressed replay byte buffer
export default class ReplayerPlayback {
  constructor(buffer) {
    const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer)
    this.decoded = decodeReplayBinary(bytes)
    this.players = normalizePlayers(this.decoded)
    this.policy = buildPolicy(this.decoded)
    this.tickMs = Math.round(1000 / this.decoded.tickRateHz) || TICK_MS
    this.world = null
    this.pendingRespawns = []
    this.clock = { ms: 0 }
    this.appliedSteps = 0
  }

  get durationTicks() {
    return this.decoded.durationTicks
  }

  makeCtx() {
    return {
      now: () => this.clock.ms,
      broadcast: () => { },
      broadcastScore: () => { },
      broadcastHudFeed: () => { },
      hitShip: (playerId, hitBy) => {
        const ship = this.world.ships.get(playerId)
        if (!ship || !ship.alive) return
        this.world.hitShip(ship, hitBy)
      },
      scheduleRespawn: (playerId, delayMs) => {
        this.pendingRespawns.push({ playerId, atMs: this.clock.ms + delayMs })
      }
    }
  }

  ringSpawnForPlayer(playerId) {
    const player = this.players.find(p => String(p.id) === String(playerId)) ?? null
    const respawnPos = this.policy.getRespawnPosition({
      worldWidth: WORLD_WIDTH,
      worldHeight: WORLD_HEIGHT,
      player,
      players: this.players,
      world: this.world
    })
    if (respawnPos) return respawnPos
    const n = this.players.length
    const layout = this.policy.getSpawnPositions({
      worldWidth: WORLD_WIDTH,
      worldHeight: WORLD_HEIGHT,
      playerCount: n,
      players: this.players
    })
    const i = this.players.findIndex(p => String(p.id) === String(playerId))
    if (i >= 0 && i < layout.length) return layout[i]
    return layout[0] ?? { x: WORLD_WIDTH / 2, y: WORLD_HEIGHT / 2, rotation: 0 }
  }

  resetSimulation() {
    this.pendingRespawns = []
    this.clock.ms = 0
    const rosterIds = this.decoded.rosterIds
    const policy = buildPolicy(this.decoded)
    this.policy = policy
    this.world = new GameWorld({
      worldBounds: { width: WORLD_WIDTH, height: WORLD_HEIGHT, toroidalMap: this.decoded.toroidalMap },
      mode: policy,
      matchSeed: this.decoded.matchSeed >>> 0,
      replayPlayerOrder: rosterIds,
      now: () => this.clock.ms,
      onEvent: () => { }
    })
    const ctx = this.makeCtx()
    policy.ctx = ctx
    const layout = policy.getSpawnPositions({
      worldWidth: WORLD_WIDTH,
      worldHeight: WORLD_HEIGHT,
      playerCount: this.players.length,
      players: this.players
    })
    for (let i = 0; i < this.players.length; i++) {
      const p = this.players[i]
      const s = layout[i] ?? layout[0]
      this.world.addShip({
        id: p.id,
        position: { x: s.x, y: s.y },
        rotation: s.rotation ?? 0,
        color: p.color ?? PLAYER_COLORS[p.slot] ?? '#fff',
        name: p.name,
        teamId: p.teamId ?? null,
        teamName: p.teamName ?? null
      })
    }
    policy.onGameStart(this.world, this.players, ctx)
    this.appliedSteps = 0
  }

  // Apply one recorded tick row (`frameIndex` is 0-based).
  stepTick(frameIndex) {
    this.clock.ms = frameIndex * this.tickMs
    this.pendingRespawns = this.pendingRespawns.filter(r => {
      if (r.atMs <= this.clock.ms) {
        const pos = this.ringSpawnForPlayer(r.playerId)
        this.world.respawnShip(r.playerId, pos, {
          invulnerableMs: this.policy.spawnInvulnerabilityDuration
        })
        return false
      }
      return true
    })
    const row = this.decoded.ticks[frameIndex]
    if (!row) return
    const roster = this.decoded.rosterIds
    for (let p = 0; p < roster.length; p++) {
      const keys = row[p] ?? { left: false, right: false, up: false, down: false, firePresses: 0 }
      this.world.setInput(roster[p], keys, frameIndex, this.clock.ms)
    }
    this.world.update(1)
    this.appliedSteps = frameIndex + 1
  }

  // Land state after `stepCount` simulation ticks (0 = lobby spawned only).
  seek(stepCount) {
    const n = Math.max(0, Math.min(stepCount, this.durationTicks))
    this.resetSimulation()
    for (let i = 0; i < n; i++) {
      this.stepTick(i)
    }
    this.clock.ms = n * this.tickMs
    this.appliedSteps = n
  }

  getSnapshot() {
    if (!this.world) return null
    return this.world.getSnapshot(this.clock.ms)
  }
}
